import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import {
  businessMatchingDraftFirstBusinessEntityInfoApplicationsPath,
  businessMatchingOwnedBusinessEntitiesPath,
  businessMatchingRootPath,
  validateBusinessMatchingOwnedBusinessEntitiesPath,
} from "../../../../routes";
import {
  Flash,
  GroupedPrefectures,
  SharedArchitecturalSoftwares,
} from "../../../shared/lib/types";
import Background from "../../shared/components/ui/Background";
import { Box } from "../../shared/components/ui/chakra";
import BackgroundBuildingImage from "../../shared/components/app/BackgroundBuildingImage";
import GeneralHeader from "../../shared/components/app/GeneralHeader";
import GoBackLink from "../../shared/components/app/GoBackLink";
import ShadowCard from "../../shared/components/app/ShadowCard";
import Application from "../../shared/components/layouts/Application";
import { Options, SharedApprovedCurrentUser } from "../../shared/lib/types";
import useRequest from "../../shared/lib/useRequest";
import Confirmation from "./components/Confirmation";
import Step1 from "./components/Step1";
import Step2 from "./components/Step2";
import { handleValidateEmailResponse } from "./lib/handleValidateEmailResponse";
import { DraftContent } from "./lib/types";
import { Step1FormData, useStep1Form } from "./lib/useStep1Form";
import { Step2FormData, useStep2Form } from "./lib/useStep2Form";
import { openPreview } from "../../../shared/lib/openPreview";
import useFlash from "../../shared/lib/useFlash";
import {
  createUnifiedFile,
  UnifiedFile,
} from "../../../shared/lib/UnifiedFile";
import { objectPick } from "../../../shared/lib/objectPick";
import step2DefaultValue from "./lib/step2DefaultValue";
import StickyFooter from "./components/StickyFooter";
import { businessTypes } from "../../../shared/lib/businessTypes";
import { deepmergeCustom } from "deepmerge-ts";

import { Container } from "@chakra-ui/react";

const deepmerge = deepmergeCustom({
  // 配列はマージせず上書き
  mergeArrays: (values) => {
    return values[1];
  },
});
type Props = {
  flash: Flash;
  currentUser: SharedApprovedCurrentUser;
  currentUserIndustryId: number | null;
  prefectures: Options;
  industries: Options;
  businessFields: Options;
  architecturalSoftwares: SharedArchitecturalSoftwares;
  groupedPrefectures: GroupedPrefectures;
  buildingTypes: Options;
  structureTypes: Options;
  draftContent: DraftContent;
};

const OwnedBusinessEntitiesNew = ({
  flash,
  currentUser,
  currentUserIndustryId,
  prefectures,
  industries,
  businessFields,
  architecturalSoftwares,
  groupedPrefectures,
  buildingTypes,
  structureTypes,
  draftContent,
}: Props) => {
  const defaultValues = {
    business_type: "",
    name: "",
    name_kana: "",
    branch_name: "",
    postal_code: "",
    prefecture_id: "",
    address1: "",
    address2: "",
    address3: "",
    display_address_restricted: false,
    phone_number: "",
    email: currentUser.email,
    industry_id: currentUserIndustryId ? currentUserIndustryId.toString() : "",
    number_of_employees: "",
    establishment_year: "",
    establishment_month: "",
    capital_ten_thousand_yen: "",
    president_name: "",
    website_url: "",
    facebook_url: "",
    instagram_url: "",
    note_url: "",
    pinterest_url: "",
    threads_url: "",
    tiktok_url: "",
    x_url: "",
    youtube_url: "",
    logo_image: null,
  };
  const step1Methods = useStep1Form({
    defaultValues: deepmerge(
      {},
      defaultValues,
      objectPick(draftContent ?? {}, Object.keys(defaultValues)),
    ),
  });
  const step2DefaultValues = deepmerge(
    {},
    step2DefaultValue,
    objectPick(draftContent ?? {}, Object.keys(step2DefaultValue)),
  );
  const step2Methods = useStep2Form({
    defaultValues: step2DefaultValues,
  });
  const {
    handleSubmit: handleFinalFormSubmit,
    formState: { isSubmitting: isFinalFormSubmitting },
  } = useForm();
  const request = useRequest();
  const [formData, setFormData] = useState<{
    step1: Step1FormData | undefined;
    step2: Step2FormData | undefined;
  }>({ step1: undefined, step2: undefined });

  const defaultDesignedArchitecturalWorksImages =
    draftContent?.contractor_info_attributes?.architectural_design_works_attributes?.map(
      (architecturalDesignWork) =>
        architecturalDesignWork.images.map((image) => createUnifiedFile(image)),
    ) ?? [[]];
  const [
    designedArchitecturalWorksImages,
    setDesignedArchitecturalWorksImages,
  ] = useState<UnifiedFile[][]>(
    defaultDesignedArchitecturalWorksImages.length === 0
      ? [[]]
      : defaultDesignedArchitecturalWorksImages,
  );

  useEffect(() => {
    if (window.location.hash !== "") {
      window.location.hash = "";
    }
  }, []);

  const onBeforeUnload = useCallback((e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = "";
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const step = location.hash;

  useEffect(() => {
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [onBeforeUnload]);

  const onStep2 = async (data: Step1FormData) => {
    if (!(await validateEmail(data))) {
      return;
    }
    setFormData((prev) => ({ ...prev, step1: data }));

    await navigate("#step2");
    window.scrollTo(0, 0);
  };

  const onConfirm = async (data: Step2FormData) => {
    await navigate("#confirm");
    setFormData((prev) => ({ ...prev, step2: data }));
    window.scrollTo(0, 0);
  };

  const validateEmail = async (data: Step1FormData) => {
    const res = await request(
      validateBusinessMatchingOwnedBusinessEntitiesPath(),
      "POST",
      { business_entity_info_application: { email: data.email } },
    );
    return await handleValidateEmailResponse({
      response: res,
      methods: {
        setError: step1Methods.setError,
        clearErrors: step1Methods.clearErrors,
      },
    });
  };
  const showFlash = useFlash();

  const requestDataWithSignedIds = async ({
    step1,
    step2,
  }: {
    step1: Step1FormData;
    step2: Step2FormData;
  }) => {
    const { uploadedFile: logoImage, error: logoImageError } =
      await createUnifiedFile(step1.logo_image).upload();
    if (logoImageError != null) {
      showFlash({ error: logoImageError });
      return { isError: true };
    }

    const designedArchitecturalWorksImagesUploadResults = await Promise.all(
      designedArchitecturalWorksImages.map((files) =>
        Promise.all(files.map((file) => file.upload())),
      ),
    );

    if (
      designedArchitecturalWorksImagesUploadResults.some((result) =>
        result.some((r) => r.error != null),
      )
    ) {
      showFlash({ error: "ファイルのアップロードに失敗しました" });
      return { isError: true };
    }

    const client_info_attributes = step2.client_info_attributes;

    const contractor_info_attributes = step2.contractor_info_attributes
      ? {
          ...step2.contractor_info_attributes,
          architectural_design_works_attributes:
            designedArchitecturalWorksImagesUploadResults.map(
              (result, index) => {
                if (step2?.contractor_info_attributes) {
                  return {
                    ...step2.contractor_info_attributes
                      .architectural_design_works_attributes[index],
                    images: result.map((r) => r.uploadedFile?.signedId),
                  };
                }
              },
            ),
        }
      : null;

    return {
      content: {
        ...step1,
        logo_image: logoImage.signedId,
        ...{
          ...step2,
          contractor_info_attributes,
          client_info_attributes,
        },
      },
    };
  };

  const onSubmit = async () => {
    if (formData.step1 == null || formData.step2 == null) return;

    if (!(await validateEmail(formData.step1))) {
      return;
    }

    const { establishment_year, establishment_month } = formData.step1;
    const requestData = await requestDataWithSignedIds({
      step1: formData.step1,
      step2: formData.step2,
    });
    if (requestData.isError != null) return;

    if (formData.step2.use_case === "client") {
      requestData.content.contractor_info_attributes = null;
    }

    if (formData.step2.use_case === "contractor") {
      requestData.content.client_info_attributes = null;
    }

    const res = await request(
      businessMatchingOwnedBusinessEntitiesPath(),
      "POST",
      {
        first_business_entity_info_application: {
          ...requestData.content,
          establishment_year_month: `${establishment_year}/${establishment_month}`,
        },
      },
    );
    if (res.ok) {
      window.removeEventListener("beforeunload", onBeforeUnload);
      const json = (await res.json()) as { redirect_url: string };
      window.location.href = json.redirect_url;
    }
  };

  const onClickPreview = () => {
    void openPreview({
      businessEntity: {
        ...step1Methods.getValues(),
        ...step2Methods.getValues(),
      },
      designedArchitecturalWorksImages: designedArchitecturalWorksImages.map(
        (files) =>
          files.map((file) => ({
            url: file.url,
            signed_id: file.signedId,
          })),
      ),
    });
  };

  const draft = async () => {
    const step1Value = step1Methods.getValues();
    const step2Value = step2Methods.getValues();

    const requestData = await requestDataWithSignedIds({
      step1: step1Value,
      step2: step2Value,
    });
    if (requestData.isError) return;

    const res = await request(
      businessMatchingDraftFirstBusinessEntityInfoApplicationsPath(),
      "POST",
      {
        draft_first_business_entity_info_application: requestData.content,
      },
    );

    if (res.ok) {
      showFlash({
        success: "下書き保存しました",
      });
    }
  };

  return (
    <Box>
      <Box>
        <Application flash={flash} currentUser={currentUser}>
          <Box position="relative">
            <BackgroundBuildingImage />
            <GeneralHeader currentUser={currentUser} />
            <Background>
              <Container
                maxW="container.md"
                mt={{ base: 6, lg: 20 }}
                zIndex={1}
              >
                {step === "" ? (
                  <GoBackLink href={businessMatchingRootPath()}>
                    事業者一覧に戻る
                  </GoBackLink>
                ) : step === "#step2" ? (
                  <GoBackLink onClick={() => navigate(-1)}>
                    事業者情報の入力に戻る
                  </GoBackLink>
                ) : (
                  <></>
                )}
                <Box mt={2}>
                  <ShadowCard px={{ base: 4, md: 14 }}>
                    <Box display={step === "" ? "block" : "none"}>
                      <Step1
                        industries={industries}
                        prefectures={prefectures}
                        methods={step1Methods}
                        onSubmit={step1Methods.handleSubmit(onStep2)}
                      />
                    </Box>
                    <Box display={step === "#step2" ? "block" : "none"}>
                      <Step2
                        industries={industries}
                        architecturalSoftwares={architecturalSoftwares}
                        businessFields={businessFields}
                        groupedPrefectures={groupedPrefectures}
                        prefectures={prefectures}
                        buildingTypes={buildingTypes}
                        structureTypes={structureTypes}
                        methods={step2Methods}
                        designedArchitecturalWorksImages={
                          designedArchitecturalWorksImages
                        }
                        setDesigendArchitecturalWorkImages={
                          setDesignedArchitecturalWorksImages
                        }
                        onSubmit={onConfirm}
                        onClickBack={() => navigate(-1)}
                      />
                    </Box>
                    {step === "#confirm" && (
                      <Confirmation
                        values={{
                          ...step1Methods.getValues(),
                          ...step2Methods.getValues(),
                        }}
                        businessTypes={businessTypes}
                        prefectures={prefectures}
                        industries={industries}
                        businessFields={businessFields}
                        buildingTypes={buildingTypes}
                        structureTypes={structureTypes}
                        architecturalSoftwares={architecturalSoftwares}
                        designedArchitecturalWorksImageUrls={designedArchitecturalWorksImages.map(
                          (files) => files.map((file) => file.url),
                        )}
                        isSubmitting={isFinalFormSubmitting}
                        onSubmit={handleFinalFormSubmit(onSubmit)}
                        onClickFix={() => navigate(-1)}
                        onClickPreview={onClickPreview}
                      />
                    )}
                  </ShadowCard>
                </Box>
              </Container>
            </Background>
            {(step === "" || step === "#step2") && (
              <Box position="sticky" bottom={0} zIndex={1}>
                <StickyFooter onDraft={draft} onPreview={onClickPreview} />
              </Box>
            )}
          </Box>
        </Application>
      </Box>
    </Box>
  );
};

const BrowserRouterWrapper = (props: Props) => {
  return (
    <BrowserRouter>
      <OwnedBusinessEntitiesNew {...props} />
    </BrowserRouter>
  );
};

export default BrowserRouterWrapper;
